import { Box } from '@mui/material';
import { Button, Icon, IconButton, Input, Loader, Typography, Dialog } from '@sm-dashboard/ui-components';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import instanceAxios from '@/util/axios';
import {
  archiveAddUrl,
  archiveDataAddUrl,
  archiveDataUpdateUrl,
  archiveDataUrl,
  archiveDeleteUrl,
  archiveUpdateUrl,
  archiveUrl,
  archiveUploadCoverUrl,
  resortingUpdateUrl,
  archiveCloneFoldersUrl
} from '@/constants';
import { ArchiveData, ArchiveMeta } from '@sm-dashboard/types';
import { useEffect, useState } from 'react';
import DynamicDialog from '../DaynmicDialog';
import { useFetch } from '@/hooks/useFetch';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import CopyAll from '@mui/icons-material/CopyAll';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TagsAllow from './ArchiveTags';
import LockIcon from '@mui/icons-material/Lock';
import { LongMenu } from './LongMenu';
import { useAppContext } from '@/context/context';
import { ArchiveType } from '@sm-dashboard/types';

/** Local extension of the shared ArchiveData type with the per-ad AI prompt. */
export type ArchiveDataWithAiPrompt = ArchiveData & { aiPrompt?: string };

export type CurrentArchiveType = ArchiveMeta & {
  isEdit: boolean;
};
const archiveDataPayloadForUpdate = (item: ArchiveDataWithAiPrompt) => {
  const { Branch, Operation, ...rest } = item;
  let { operationId } = rest;
  if (operationId === undefined && Operation && typeof Operation === 'object' && Operation.id != null) {
    operationId = Operation.id;
  }
  if (operationId !== undefined && operationId !== null) {
    operationId = Number(operationId);
  }
  return { ...rest, operationId };
};
export const addArchiveMeta = async (currentItem: CurrentArchiveType | null, callback: () => void) => {
  if (currentItem) {
    const { isEdit, createdAt, updatedAt, ...archiveMeta } = currentItem;
    await instanceAxios.post(archiveAddUrl, archiveMeta);
    callback();
  }
};

export const createArchiveMeta = async (
  currentItem: Omit<CurrentArchiveType, 'isEdit'> & { isEdit?: boolean; coverImage?: string | null }
): Promise<ArchiveMeta> => {
  const payload = { ...currentItem } as Record<string, unknown>;
  delete payload.isEdit;
  delete payload.createdAt;
  delete payload.updatedAt;
  const response = await instanceAxios.post(archiveAddUrl, payload);
  return response.data as ArchiveMeta;
};

export const updateArchiveMeta = async (currentItem: CurrentArchiveType | null, callback: () => void) => {
  if (currentItem) {
    const { isEdit, createdAt, updatedAt, ...archiveMeta } = currentItem;
    await instanceAxios.post(archiveUpdateUrl, archiveMeta);
    callback();
  }
};

export const deleteArchiveMeta = async (id: number, callback: () => void) => {
  await instanceAxios.delete(`${archiveDeleteUrl}/${id}`);
  callback();
};

export const reSortingArchiveMeta = async (data: any, callback: () => void) => {
  if (data) {
    await instanceAxios.post(resortingUpdateUrl, data);
    callback();
  }
};

export const addArchiveData = async (currentItem: ArchiveDataWithAiPrompt, callback: () => void) => {
  await instanceAxios.post(archiveDataAddUrl, currentItem);
  callback();
};

export const updateArchiveData = async (currentItem: ArchiveDataWithAiPrompt, callback: () => void) => {
  await instanceAxios.post(archiveDataUpdateUrl, archiveDataPayloadForUpdate(currentItem));
  callback();
};

export const bulkUpdateArchiveData = async (
  items: ArchiveData[],
  updates: Partial<ArchiveData>,
  callback: () => void
) => {
  const ids = items.map(item => item.id);
  const { Branch, Operation, ...cleanUpdates } = updates as ArchiveData;
  await instanceAxios.post(archiveDataUpdateUrl, { ids, ...cleanUpdates });
  callback();
};

export const deleteArchiveData = async (id: number, callback: () => void) => {
  await instanceAxios.delete(`${archiveDataUrl}/${id}`);
  callback();
};

export const bulkDeleteArchiveData = async (ids: number[], callback: () => void) => {
  await Promise.all(ids.map(id => instanceAxios.delete(`${archiveDataUrl}/${id}`)));
  callback();
};

export const bulkDeleteArchiveMeta = async (ids: number[], callback: () => void) => {
  await Promise.all(ids.map(id => instanceAxios.delete(`${archiveDeleteUrl}/${id}`)));
  callback();
};

export type DeleteOrUpdateTriggersProps = {
  onDelete: () => void | Promise<void>;
  onUpdate: () => void;
  item?: ArchiveMeta;
  fetchContentData?: () => Promise<void>;
  showMoveFolder?: boolean;
  onSingleItemMoveStart?: () => void;
  /** Quiet text-style button (e.g. next to a title) instead of the default filled one. Placement is up to the parent. */
  quiet?: boolean;
};

export const DeleteOrUpdateTriggers = ({
  onDelete,
  onUpdate,
  item,
  fetchContentData,
  showMoveFolder = true,
  onSingleItemMoveStart,
  quiet = false
}: DeleteOrUpdateTriggersProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openMoveFolder, setOpenMoveFolder] = useState(false);
  const [openAllowTagsDialog, setOpenAllowTagsDialog] = useState(false);
  const [openCoverDialog, setOpenCoverDialog] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string | null>(item?.coverImage || null);
  const [coverLoading, setCoverLoading] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const { setSnackbar } = useAppContext();

  useEffect(() => {
    setCoverPreview(item?.coverImage || null);
  }, [item?.coverImage, openCoverDialog]);

  const handleCloneFolder = async () => {
    if (!item) return;
    setIsCloning(true);
    // Started from the menu (which closes) — show progress until the success / error message replaces it.
    setSnackbar({ open: true, message: 'משכפל את התיקייה...', severity: 'info' });
    try {
      await instanceAxios.post(archiveCloneFoldersUrl, {
        folderIds: [item.id],
        destinationParentId: item.parent || 0,
        nameSuffix: ' (העתק)'
      });
      setSnackbar({ open: true, message: `התיקייה שוכפלה בהצלחה`, severity: 'success' });
      fetchContentData?.();
    } catch (error) {
      console.error('שגיאה בשכפול תיקייה:', error);
      setSnackbar({ open: true, message: 'שגיאה בשכפול התיקייה', severity: 'error' });
    } finally {
      setIsCloning(false);
    }
  };

  const uploadCover = async (file: File) => {
    if (!item?.id) return;
    setCoverLoading(true);
    try {
      const formData = new FormData();
      formData.append('fileInput', file);
      const uploadRes = await instanceAxios.post(archiveUploadCoverUrl, formData);
      const url = uploadRes.data?.url as string;
      await instanceAxios.post(archiveUpdateUrl, {
        id: item.id,
        parent: item.parent,
        sorting: item.sorting,
        folderName: item.folderName,
        archiveType: item.archiveType,
        tagsAllow: item.tagsAllow,
        coverImage: url
      });
      setCoverPreview(url);
      setSnackbar({ open: true, message: 'תמונת התיקייה נשמרה', severity: 'success' });
      fetchContentData?.();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'שגיאה בשמירת תמונת התיקייה', severity: 'error' });
    } finally {
      setCoverLoading(false);
    }
  };

  const removeCover = async () => {
    if (!item?.id) return;
    setCoverLoading(true);
    try {
      await instanceAxios.post(archiveUpdateUrl, {
        id: item.id,
        parent: item.parent,
        sorting: item.sorting,
        folderName: item.folderName,
        archiveType: item.archiveType,
        tagsAllow: item.tagsAllow,
        coverImage: null
      });
      setCoverPreview(null);
      setSnackbar({ open: true, message: 'תמונת התיקייה הוסרה', severity: 'success' });
      fetchContentData?.();
    } catch (error) {
      console.error(error);
      setSnackbar({ open: true, message: 'שגיאה בהסרת תמונת התיקייה', severity: 'error' });
    } finally {
      setCoverLoading(false);
    }
  };

  const actions = [
    {
      label: 'עריכה',
      onClick: onUpdate,
      icon: <ModeEditOutlineOutlinedIcon fontSize="small" />
    },
    ...(item?.archiveType === ArchiveType.fileArchive
      ? [
          {
            label: 'תמונת תיקייה',
            onClick: () => setOpenCoverDialog(true),
            icon: <ImageOutlinedIcon fontSize="small" />
          }
        ]
      : []),
    {
      label: 'שכפל תיקייה',
      onClick: handleCloneFolder,
      icon: <CopyAll fontSize="small" />
    },
    ...(showMoveFolder
      ? [
          {
            label: 'הזזה בין תיקיות',
            onClick: () => {
              onSingleItemMoveStart?.();
              setOpenMoveFolder(true);
            },
            icon: <DriveFileMoveIcon fontSize="small" />
          }
        ]
      : []),
    {
      label: 'הגדרות הרשאות',
      onClick: () => setOpenAllowTagsDialog(true),
      icon: <LockIcon fontSize="small" />
    },
    // Destructive action last.
    {
      label: 'מחיקה',
      onClick: () => setOpenDialog(true),
      icon: <ClearOutlinedIcon fontSize="small" />
    }
  ];

  return (
    <Box sx={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
      <LongMenu
        actions={actions}
        {...(quiet && {
          buttonVariant: 'text',
          buttonColor: 'secondary',
          buttonSize: 'medium',
          buttonStyle: { marginRight: 0 }
        })}
      />

      <Dialog
        open={openCoverDialog}
        onClose={() => setOpenCoverDialog(false)}
        title="תמונת תיקייה"
        cancelAction={{ label: 'סגור' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', minWidth: 260 }}>
          {coverPreview ? (
            <Box
              component="img"
              src={coverPreview}
              alt="תמונת תיקייה"
              sx={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 2 }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              לא הוגדרה תמונה
            </Typography>
          )}
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id={`folder-cover-input-${item?.id}`}
            type="file"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) uploadCover(file);
            }}
          />
          <Button
            loading={coverLoading}
            onClick={() => document.getElementById(`folder-cover-input-${item?.id}`)?.click()}
          >
            בחר תמונה
          </Button>
          {coverPreview && (
            <Button variant="outlined" loading={coverLoading} onClick={removeCover}>
              הסר תמונה
            </Button>
          )}
        </Box>
      </Dialog>

      <TagsAllow
        open={openAllowTagsDialog}
        onClose={() => setOpenAllowTagsDialog(false)}
        itemId={item?.id}
        existingTags={item?.tagsAllow}
        item={item}
        onSuccess={() => {
          fetchContentData?.();
        }}
      />

      <DynamicDialog
        open={openDialog}
        title="מחיקת תיקייה"
        subtitle="האם אתה בטוח שברצונך למחוק את התיקייה ? (כלל הקבצים ימחקו)"
        handleOkClose={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDialog(false);
          } finally {
            setIsDeleting(false);
          }
        }}
        handleNotOkClose={() => setOpenDialog(false)}
        loading={isDeleting}
      />
      {item && fetchContentData && (
        <MoveFolder
          open={openMoveFolder}
          setOpen={setOpenMoveFolder}
          item={item}
          isArchiveData={false}
          fetchContentData={fetchContentData}
        />
      )}
    </Box>
  );
};

export const AddOrUpdateTriggers = ({
  item,
  loading,
  handleChange,
  onSave,
  onCancel,
  layout = 'stack'
}: {
  item: any;
  loading: boolean;
  handleChange: (event: React.SyntheticEvent) => void;
  onSave: () => void;
  onCancel: () => void;
  /** `stack`: centered field over the buttons. `inline`: field and buttons in one row. */
  layout?: 'stack' | 'inline';
}) => {
  const isInline = layout === 'inline';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isInline ? 'row' : 'column',
        alignItems: 'center',
        gap: isInline ? 0.75 : 0.5,
        width: '100%'
      }}
    >
      <Box sx={isInline ? { flex: 1, minWidth: 0 } : { width: '70%' }}>
        <Input
          appearance="dashed"
          value={item?.folderName}
          inputProps={isInline ? undefined : { style: { textAlign: 'center' } }}
          onChange={handleChange}
          onKeyDown={e => {
            if (e.key === 'Enter' && !loading) onSave();
            if (e.key === 'Escape') onCancel();
          }}
          autoFocus
        />
      </Box>
      <Box sx={{ display: 'flex', gap: isInline ? 0.5 : 1, flexShrink: 0 }}>
        <IconButton
          variant="contained"
          color="primary"
          shape="circle"
          size="small"
          aria-label="שמירה"
          disabled={loading}
          onClick={onSave}
        >
          {loading ? <Loader size={12} /> : <Icon icon="check" />}
        </IconButton>
        <IconButton
          variant="contained"
          color="secondary"
          shape="circle"
          size="small"
          aria-label="ביטול"
          disabled={loading}
          onClick={onCancel}
        >
          <Icon icon="closeOutlined" />
        </IconButton>
      </Box>
    </Box>
  );
};

export const MoveFolder = ({
  open,
  setOpen,
  item,
  isArchiveData,
  fetchContentData,
  bulkItems,
  bulkFolders
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  item: ArchiveData | ArchiveMeta;
  isArchiveData: boolean;
  fetchContentData: () => Promise<void>;
  bulkItems?: ArchiveData[];
  bulkFolders?: ArchiveMeta[];
}) => {
  const { loading, data: archiveMetaList, fetchData } = useFetch();
  const [currentArchiveMeta, setCurrentArchiveMeta] = useState<ArchiveMeta | null>(null);
  const [archiveMetaListUndo, setArchiveMetaListUndo] = useState<ArchiveMeta[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>(undefined);
  const [loadingMoveFolder, setLoadingMoveFolder] = useState(false);
  const [checkingFolderId, setCheckingFolderId] = useState<number | null>(null);
  const { setSnackbar } = useAppContext();

  const showRootFolderOption = bulkItems?.length
    ? !bulkItems.every(file => file.folderId === 0)
    : bulkFolders?.length
    ? !bulkFolders.every(folder => folder.parent === 0)
    : isArchiveData
    ? (item as ArchiveData).folderId !== 0
    : (item as ArchiveMeta).parent !== 0;

  const destinationFolderId = selectedTargetId !== undefined ? selectedTargetId : currentArchiveMeta?.id;
  const canConfirmMove = selectedTargetId !== undefined || Boolean(currentArchiveMeta?.id);

  useEffect(() => {
    if (open) {
      setCurrentArchiveMeta(null);
      setArchiveMetaListUndo([]);
      setSelectedTargetId(undefined);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchData({
        method: 'POST',
        url: archiveUrl,
        data: { archiveType: currentArchiveMeta?.archiveType || item.archiveType, parent: currentArchiveMeta?.id || 0 }
      });
    }
  }, [currentArchiveMeta, open]);

  useEffect(() => {
    if (currentArchiveMeta) {
      setArchiveMetaListUndo(prevItems => {
        const itemExists = prevItems.some(item => item.id === currentArchiveMeta.id);

        if (!itemExists) {
          return [...prevItems, currentArchiveMeta];
        }
        return prevItems;
      });
    }
  }, [currentArchiveMeta]);

  const stopBubble = (e: React.SyntheticEvent) => e.stopPropagation();
  const dialogPointerHandlers = { onMouseDown: stopBubble, onClick: stopBubble };

  const resetAndCloseDialog = () => {
    setCurrentArchiveMeta(null);
    setArchiveMetaListUndo([]);
    setSelectedTargetId(undefined);
    setOpen(false);
  };

  const handleUndoArchiveMeta = () => {
    archiveMetaListUndo.pop();
    setCurrentArchiveMeta(archiveMetaListUndo[archiveMetaListUndo.length - 1] || null);
    setSelectedTargetId(undefined);
  };

  // Rows run edge to edge (full-width hover / selection). The folder IconButton brings ~8px of its own
  // padding, so a 16px start keeps the icon aligned with the dialog title and footer (24px).
  const pickerRowSx = (selected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    cursor: 'pointer',
    paddingInlineStart: 2,
    paddingInlineEnd: 3,
    py: 0.25,
    // Selected destination: primary tint, like the design-system Select's selected option.
    bgcolor: selected ? 'primary.light' : 'transparent',
    '&:hover': { bgcolor: selected ? 'primary.light' : 'action.hover' }
  });

  const renderPickerRow = (
    key: React.Key,
    label: string,
    selected: boolean,
    onSelect: () => void,
    onDrillIn?: () => void
  ) => (
    <Box
      key={key}
      onClick={e => {
        stopBubble(e);
        if (onDrillIn) {
          onDrillIn();
        } else {
          onSelect();
        }
      }}
      sx={pickerRowSx(selected)}
    >
      <IconButton
        shape="circle"
        color="primary"
        size="medium"
        aria-label="פתיחת תיקייה"
        onClick={e => {
          stopBubble(e);
          onDrillIn?.();
        }}
        tabIndex={onDrillIn ? 0 : -1}
      >
        <FolderIcon />
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0, ...(selected && { fontWeight: 600 }) }}>
        <Typography variant="body" color={selected ? 'primary.main' : 'text.secondary'}>
          {label}
        </Typography>
      </Box>
      {checkingFolderId === key && <Loader size={16} />}
    </Box>
  );

  // Folders with subfolders open on click; a folder without subfolders is a final destination —
  // clicking it toggles it as the selected target instead of opening an empty level.
  const handleFolderRowClick = async (folderItem: ArchiveMeta & { id: number }) => {
    if (checkingFolderId !== null) return;
    setCheckingFolderId(folderItem.id);
    try {
      const { data: children } = await instanceAxios.post(archiveUrl, {
        archiveType: folderItem.archiveType || item.archiveType,
        parent: folderItem.id
      });
      if (Array.isArray(children) && children.length > 0) {
        setSelectedTargetId(undefined);
        setCurrentArchiveMeta(folderItem);
      } else {
        setSelectedTargetId(prev => (prev === folderItem.id ? undefined : folderItem.id));
      }
    } catch (error) {
      console.error('שגיאה בטעינת תיקיות:', error);
    } finally {
      setCheckingFolderId(null);
    }
  };

  const handleOnSaveMoveFolder = async () => {
    if (destinationFolderId === undefined) {
      return;
    }

    const foldersToMove = bulkFolders?.length ? bulkFolders : !isArchiveData ? [item as ArchiveMeta] : [];
    if (foldersToMove.some(folder => folder.id != null && folder.id === destinationFolderId)) {
      setSnackbar({ open: true, message: 'לא ניתן להעביר תיקייה לעצמה', severity: 'warning' });
      return;
    }

    setLoadingMoveFolder(true);

    try {
      if (bulkItems && bulkItems.length > 0) {
        const firstItem = bulkItems[0];
        await instanceAxios.post(archiveDataUpdateUrl, {
          ids: bulkItems.map(item => item.id),
          folderId: destinationFolderId,
          archiveType: firstItem.archiveType,
          fileType: firstItem.fileType || 'file'
        });
      } else if (isArchiveData) {
        await instanceAxios.post(archiveDataUpdateUrl, {
          ...archiveDataPayloadForUpdate(item as ArchiveData),
          folderId: destinationFolderId
        });
      }
      if (bulkFolders && bulkFolders.length > 0) {
        await Promise.all(
          bulkFolders.map(folder =>
            instanceAxios.post(archiveUpdateUrl, {
              ...folder,
              parent: destinationFolderId
            })
          )
        );
      } else if (!isArchiveData) {
        await instanceAxios.post(archiveUpdateUrl, {
          ...item,
          parent: destinationFolderId
        });
      }

      fetchContentData();
      resetAndCloseDialog();
    } catch (error) {
      console.error('שגיאה בהעברת קבצים:', error);
      setSnackbar({ open: true, message: 'שגיאה בהעברת קבצים', severity: 'error' });
    } finally {
      setLoadingMoveFolder(false);
    }
  };

  return (
    // React events bubble out of the dialog's portal through this wrapper — keep them from reaching the card behind.
    <Box component="span" {...dialogPointerHandlers}>
      <Dialog
        fullWidth
        maxWidth="xs"
        contentPadding="none"
        open={open}
        aria-describedby="alert-dialog-dynamic"
        onClose={resetAndCloseDialog}
        title="הזזת פריט בתיקיות"
        cancelAction
        primaryAction={{
          label: 'העברה',
          onClick: handleOnSaveMoveFolder,
          loading: loadingMoveFolder,
          disabled: !canConfirmMove
        }}
      >
        <>
          {currentArchiveMeta && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                paddingInlineStart: 2,
                paddingInlineEnd: 3,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider'
              }}
            >
              <IconButton
                variant="text"
                color="secondary"
                shape="circle"
                size="medium"
                aria-label="חזור"
                onClick={e => {
                  stopBubble(e);
                  handleUndoArchiveMeta();
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
              <Typography variant="body" color="text.secondary">
                {currentArchiveMeta.folderName}
              </Typography>
            </Box>
          )}
          {loading ? (
            <Box
              sx={{ display: 'flex', width: '100%', height: '250px', alignItems: 'center', justifyContent: 'center' }}
            >
              <Loader />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '50vh', overflow: 'auto', py: 1 }}>
              {showRootFolderOption &&
                !currentArchiveMeta &&
                renderPickerRow('root', 'תיקייה ראשית', selectedTargetId === 0, () =>
                  setSelectedTargetId(prev => (prev === 0 ? undefined : 0))
                )}
              {(archiveMetaList as ArchiveMeta[] | undefined)
                ?.filter((folderItem): folderItem is ArchiveMeta & { id: number } => folderItem.id != null)
                .map(folderItem =>
                  renderPickerRow(
                    folderItem.id,
                    folderItem.folderName,
                    selectedTargetId === folderItem.id,
                    () => handleFolderRowClick(folderItem),
                    () => handleFolderRowClick(folderItem)
                  )
                )}
            </Box>
          )}
        </>
      </Dialog>
    </Box>
  );
};
